
import React, { createContext, useContext, useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import { auth } from "../firebase";
import { logLogin, logLogout } from "../services/activityLog";
import { getRoleByEmail, UserRole, isUserSuspended, canUseDebugView } from "../config/roles";

export type { UserRole };

export type AppUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole; 
  displayName: string;
};

type AuthContextValue = {
  user: AppUser | null;
  isAuthenticated: boolean;
  isSealUser: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  debugViewDepartmentId: string | null;
  setDebugViewDepartmentId: (id: string | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function mapFirebaseUser(u: firebase.User | null): AppUser | null {
  if (!u) return null;
  const email = u.email || "";
  const role = getRoleByEmail(email);
  
  // Try to use displayName from Firebase, fallback to email prefix
  let displayName = u.displayName;
  if (!displayName && email) {
      displayName = email.split("@")[0];
  }
  
  return {
    id: u.uid,
    name: displayName || "User",
    email: email,
    role: role, 
    displayName: displayName || "User",
  };
}

// Fetch IP from Cloudflare trace and cache it locally
// Cloudflare Pages specific endpoint
async function fetchClientIpFromCloudflare(): Promise<string | undefined> {
  if (typeof window === "undefined") return undefined;

  try {
    const response = await fetch("/cdn-cgi/trace", { method: "GET" });
    if (!response.ok) {
      // Local development might fail this call, which is fine
      return undefined;
    }

    const text = await response.text();
    const ipLine = text.split("\n").find((line) => line.startsWith("ip="));
    const ip = ipLine?.split("=")[1]?.trim();

    if (ip) {
      console.log("[AuthContext] Client IP detected (CF):", ip);
      try {
        window.localStorage.setItem("clientIp", ip);
      } catch (storageError) {
        console.warn("[AuthContext] Failed to store clientIp in localStorage", storageError);
      }
      return ip;
    }

    return undefined;
  } catch (error) {
    // Silent fail in dev usually
    return undefined;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugViewDepartmentId, _setDebugViewDepartmentId] = useState<string | null>(null);

  useEffect(() => {
    // 1. Initialize Firebase Auth Listener
    if (!auth) {
      console.warn("[AuthContext] Firebase Auth not initialized.");
      setLoading(false);
      return;
    }

    // Always try to fetch IP on app mount to ensure logs have it immediately
    fetchClientIpFromCloudflare();

    const unsub = auth.onAuthStateChanged((firebaseUser) => {
      // Check suspension
      if (firebaseUser && isUserSuspended(firebaseUser.email)) {
          console.warn(`[Auth] User suspended: ${firebaseUser.email}`);
          alert("此帳號目前停用，請聯繫法務或系統管理者。");
          auth.signOut().then(() => {
              setUser(null);
              _setDebugViewDepartmentId(null);
              setLoading(false);
          });
          return;
      }

      setUser(mapFirebaseUser(firebaseUser));
      // Reset debug view on user change
      _setDebugViewDepartmentId(null);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const isAuthenticated = !!user;
  const isSealUser = user?.role === 'sealUser';

  async function login(email: string, password: string) {
    if (!auth) {
      throw new Error("Firebase Auth service is unavailable.");
    }
    // Pre-check suspension before attempting auth
    if (isUserSuspended(email)) {
        throw new Error("此帳號目前停用，請聯繫法務或系統管理者。");
    }

    try {
      const result = await auth.signInWithEmailAndPassword(email, password);
      if (result.user) {
        if (isUserSuspended(result.user.email)) {
             throw new Error("此帳號目前停用。");
        }
        
        // Refresh IP on login to be sure
        await fetchClientIpFromCloudflare();
        
        // Log Login
        await logLogin({ uid: result.user.uid, email: result.user.email });
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async function logout() {
    if (!auth) return;
    if (auth.currentUser) {
      try {
        await logLogout({ uid: auth.currentUser.uid, email: auth.currentUser.email });
      } catch (e) { console.warn(e); }
    }
    await auth.signOut();
    _setDebugViewDepartmentId(null);
  }

  const setDebugViewDepartmentId = (id: string | null) => {
      if (canUseDebugView(user?.email)) {
          _setDebugViewDepartmentId(id);
      } else {
          _setDebugViewDepartmentId(null);
      }
  };

  if (loading) {
    return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0f0f10", color: "#fff" }}>
            Loading...
        </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isSealUser, login, logout, debugViewDepartmentId, setDebugViewDepartmentId }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
